import React, { createContext, useReducer, useContext, ReactNode, Dispatch } from 'react';
import { type User, type DateRange, type ServiceData, UserRoles } from '../types';

// Service cache structure
interface ServiceCache {
  [service: string]: ServiceData;
}

// User cache structure
interface UserCache {
  [email: string]: ServiceCache;
}

// App State interface
export interface AppState {
  currentUser: User | null;
  userCache: UserCache;
  activeEmail: string | null;
  currentView: 'dashboard' | 'admin';
  searchMode: boolean;
  searchEmail: string;
  searchedUserName: string;
  showLogoutPage: boolean;
  dateRange: DateRange;
  timeStep: number;
}

// Action Types
export const ActionTypes = {
  // User Data
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_USER_DATA: 'SET_USER_DATA',
  SET_USER_LOADING: 'SET_USER_LOADING',
  SET_USER_ERROR: 'SET_USER_ERROR',

  // Service Data (Zoom, Zscaler, Mist, Alerts)
  SET_SERVICE_DATA: 'SET_SERVICE_DATA',
  SET_SERVICE_LOADING: 'SET_SERVICE_LOADING',
  SET_SERVICE_ERROR: 'SET_SERVICE_ERROR',

  // UI State
  SET_CURRENT_VIEW: 'SET_CURRENT_VIEW',
  SET_SEARCH_MODE: 'SET_SEARCH_MODE',
  SET_SEARCH_EMAIL: 'SET_SEARCH_EMAIL',
  SET_SEARCHED_USER_NAME: 'SET_SEARCHED_USER_NAME',
  SET_SHOW_LOGOUT_PAGE: 'SET_SHOW_LOGOUT_PAGE',
  SET_DATE_RANGE: 'SET_DATE_RANGE',

  // Cache Management
  CLEAR_USER_CACHE: 'CLEAR_USER_CACHE',
  CLEAR_ALL_CACHE: 'CLEAR_ALL_CACHE',

  // SSE Streaming
  RESET_ALL_SERVICES: 'RESET_ALL_SERVICES',
  RESET_AND_START_LOADING: 'RESET_AND_START_LOADING',
} as const;

// Action interfaces
type AppAction =
  | { type: typeof ActionTypes.SET_CURRENT_USER; payload: User | null }
  | {
      type: typeof ActionTypes.SET_USER_DATA;
      payload: { email: string; service: string; data: any };
    }
  | {
      type: typeof ActionTypes.SET_USER_LOADING;
      payload: { email: string; service: string; loading: boolean };
    }
  | {
      type: typeof ActionTypes.SET_USER_ERROR;
      payload: { email: string; service: string; error: string };
    }
  | { type: typeof ActionTypes.SET_SERVICE_DATA; payload: { service: string; data: any } }
  | { type: typeof ActionTypes.SET_SERVICE_LOADING; payload: { service: string; loading: boolean } }
  | { type: typeof ActionTypes.SET_SERVICE_ERROR; payload: { service: string; error: string } }
  | { type: typeof ActionTypes.SET_CURRENT_VIEW; payload: 'dashboard' | 'admin' }
  | { type: typeof ActionTypes.SET_SEARCH_MODE; payload: boolean }
  | { type: typeof ActionTypes.SET_SEARCH_EMAIL; payload: { email: string; isSearchMode: boolean } }
  | { type: typeof ActionTypes.SET_SEARCHED_USER_NAME; payload: string }
  | { type: typeof ActionTypes.SET_SHOW_LOGOUT_PAGE; payload: boolean }
  | { type: typeof ActionTypes.SET_DATE_RANGE; payload: DateRange }
  | { type: typeof ActionTypes.CLEAR_USER_CACHE; payload: { email: string } }
  | { type: typeof ActionTypes.CLEAR_ALL_CACHE }
  | { type: typeof ActionTypes.RESET_ALL_SERVICES }
  | { type: typeof ActionTypes.RESET_AND_START_LOADING; payload: { services: readonly string[] } };

// Snap to next timeStep boundary and derive initial date range
const _initialTimeStep = Number(import.meta.env.VITE_TIME_STEP) || 15;
const _initialEnd = new Date();
_initialEnd.setMinutes(
  Math.ceil(_initialEnd.getMinutes() / _initialTimeStep) * _initialTimeStep,
  0,
  0
);
const _initialStart = new Date(_initialEnd.getTime() - 2 * 60 * 60 * 1000);

// Initial State
const initialState: AppState = {
  // Current user (logged in user)
  currentUser: {
    email: '',
    name: '',
    firstName: '',
    lastName: '',
    isAdmin: false,
    lastLogin: '',
    createdAt: '',
    userRole: UserRoles.USER,
  },

  // User data cache: { email: { data, timestamp, loading, error } }
  userCache: {},

  // Current active email being viewed
  activeEmail: null,

  // UI State
  currentView: 'dashboard',
  searchMode: false,
  searchEmail: '',
  searchedUserName: '',
  showLogoutPage: false,

  // Date range for time-based queries (shared across all users)
  dateRange: {
    start: _initialStart.toISOString(),
    end: _initialEnd.toISOString(),
  },

  timeStep: _initialTimeStep,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case ActionTypes.SET_CURRENT_USER:
      return {
        ...state,
        currentUser: action.payload,
        activeEmail: action.payload?.email || null,
      };

    case ActionTypes.SET_USER_DATA: {
      const { email, service, data } = action.payload;
      return {
        ...state,
        userCache: {
          ...state.userCache,
          [email]: {
            ...state.userCache[email],
            [service]: {
              data,
              timestamp: new Date().toISOString(),
              status: 'success',
              loading: false,
              error: null,
              expiresAt: Date.now() + 5 * 60 * 1000, // 5 minutes
            },
          },
        },
      };
    }

    case ActionTypes.SET_USER_LOADING: {
      const { email, service, loading } = action.payload;
      return {
        ...state,
        userCache: {
          ...state.userCache,
          [email]: {
            ...state.userCache[email],
            [service]: {
              ...state.userCache[email]?.[service],
              loading,
              status: loading ? 'loading' : 'idle',
            },
          },
        },
      };
    }

    case ActionTypes.SET_USER_ERROR: {
      const { email, service, error } = action.payload;
      return {
        ...state,
        userCache: {
          ...state.userCache,
          [email]: {
            ...state.userCache[email],
            [service]: {
              ...state.userCache[email]?.[service],
              loading: false,
              status: 'error',
              error,
            },
          },
        },
      };
    }

    case ActionTypes.SET_SERVICE_DATA: {
      const { service, data } = action.payload;
      const email = state.activeEmail;
      if (!email) return state;

      return {
        ...state,
        userCache: {
          ...state.userCache,
          [email]: {
            ...state.userCache[email],
            [service]: {
              data,
              timestamp: new Date().toISOString(),
              status: 'success',
              loading: false,
              error: null,
              expiresAt: Date.now() + 5 * 60 * 1000, // 5 minutes
            },
          },
        },
      };
    }

    case ActionTypes.SET_SERVICE_LOADING: {
      const { service, loading } = action.payload;
      const email = state.activeEmail;
      if (!email) return state;

      return {
        ...state,
        userCache: {
          ...state.userCache,
          [email]: {
            ...state.userCache[email],
            [service]: {
              ...state.userCache[email]?.[service],
              loading,
              status: loading ? 'loading' : 'idle',
            },
          },
        },
      };
    }

    case ActionTypes.SET_SERVICE_ERROR: {
      const { service, error } = action.payload;
      const email = state.activeEmail;
      if (!email) return state;

      return {
        ...state,
        userCache: {
          ...state.userCache,
          [email]: {
            ...state.userCache[email],
            [service]: {
              ...state.userCache[email]?.[service],
              loading: false,
              status: 'error',
              error,
            },
          },
        },
      };
    }

    case ActionTypes.SET_CURRENT_VIEW:
      return {
        ...state,
        currentView: action.payload,
      };

    case ActionTypes.SET_SEARCH_MODE:
      return {
        ...state,
        searchMode: action.payload,
      };

    case ActionTypes.SET_SEARCH_EMAIL: {
      const { email, isSearchMode } = action.payload;
      return {
        ...state,
        searchEmail: email,
        activeEmail: email || state.currentUser?.email || null,
        searchMode: isSearchMode,
      };
    }

    case ActionTypes.SET_SEARCHED_USER_NAME:
      return {
        ...state,
        searchedUserName: action.payload,
      };

    case ActionTypes.SET_SHOW_LOGOUT_PAGE:
      return {
        ...state,
        showLogoutPage: action.payload,
      };

    case ActionTypes.SET_DATE_RANGE:
      return {
        ...state,
        dateRange: action.payload,
      };

    case ActionTypes.CLEAR_USER_CACHE: {
      const { email } = action.payload;
      const newCache = { ...state.userCache };
      delete newCache[email];
      return {
        ...state,
        userCache: newCache,
      };
    }

    case ActionTypes.CLEAR_ALL_CACHE:
      return {
        ...state,
        userCache: {},
      };

    case ActionTypes.RESET_ALL_SERVICES: {
      const resetEmail = state.activeEmail;
      if (!resetEmail) return state;
      const existingCache = state.userCache[resetEmail] || {};
      // Preserve deviceInfo (session-scoped), reset all other services
      const resetCache: ServiceCache = {};
      if (existingCache.deviceInfo) {
        resetCache.deviceInfo = existingCache.deviceInfo;
      }
      return {
        ...state,
        userCache: {
          ...state.userCache,
          [resetEmail]: resetCache,
        },
      };
    }

    case ActionTypes.RESET_AND_START_LOADING: {
      const email = state.activeEmail;
      if (!email) return state;
      const existing = state.userCache[email] || {};
      // Preserve deviceInfo, reset + set loading for all specified services in one dispatch
      const newCache: ServiceCache = {};
      if (existing.deviceInfo) {
        newCache.deviceInfo = existing.deviceInfo;
      }
      for (const service of action.payload.services) {
        newCache[service] = {
          ...existing[service],
          loading: true,
          status: 'loading',
        };
      }
      return {
        ...state,
        userCache: {
          ...state.userCache,
          [email]: newCache,
        },
      };
    }

    default:
      return state;
  }
}

// Context
const AppStateContext = createContext<AppState | undefined>(undefined);
const AppDispatchContext = createContext<Dispatch<AppAction> | undefined>(undefined);

// Provider Component
interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>{children}</AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
}

// Custom Hooks
export function useAppState(): AppState {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
}

export function useAppDispatch(): Dispatch<AppAction> {
  const context = useContext(AppDispatchContext);
  if (context === undefined) {
    throw new Error('useAppDispatch must be used within an AppProvider');
  }
  return context;
}

// Helper hook for accessing both state and dispatch
export function useApp() {
  return {
    state: useAppState(),
    dispatch: useAppDispatch(),
  };
}

// Selector Hooks for specific data
export function useCurrentUser(): User | null {
  const state = useAppState();
  return state.currentUser;
}

export function useActiveUserData(): ServiceCache | null {
  const state = useAppState();
  const email = state.activeEmail;
  return email ? state.userCache[email] : null;
}

export function useServiceData(service: string): ServiceData | null {
  const state = useAppState();
  const email = state.activeEmail;
  return email ? state.userCache[email]?.[service] : null;
}

export function useUserCacheData(email: string): ServiceCache | undefined {
  const state = useAppState();
  return state.userCache[email];
}

// Action Creators
export const actions = {
  setCurrentUser: (user: User | null): AppAction => ({
    type: ActionTypes.SET_CURRENT_USER,
    payload: user,
  }),

  setUserData: (email: string, service: string, data: any): AppAction => ({
    type: ActionTypes.SET_USER_DATA,
    payload: { email, service, data },
  }),

  setUserLoading: (email: string, service: string, loading: boolean): AppAction => ({
    type: ActionTypes.SET_USER_LOADING,
    payload: { email, service, loading },
  }),

  setUserError: (email: string, service: string, error: string): AppAction => ({
    type: ActionTypes.SET_USER_ERROR,
    payload: { email, service, error },
  }),

  setServiceData: (service: string, data: any): AppAction => ({
    type: ActionTypes.SET_SERVICE_DATA,
    payload: { service, data },
  }),

  setServiceLoading: (service: string, loading: boolean): AppAction => ({
    type: ActionTypes.SET_SERVICE_LOADING,
    payload: { service, loading },
  }),

  setServiceError: (service: string, error: string): AppAction => ({
    type: ActionTypes.SET_SERVICE_ERROR,
    payload: { service, error },
  }),

  setCurrentView: (view: 'dashboard' | 'admin'): AppAction => ({
    type: ActionTypes.SET_CURRENT_VIEW,
    payload: view,
  }),

  setSearchMode: (mode: boolean): AppAction => ({
    type: ActionTypes.SET_SEARCH_MODE,
    payload: mode,
  }),

  setSearchEmail: (email: string, isSearchMode: boolean): AppAction => ({
    type: ActionTypes.SET_SEARCH_EMAIL,
    payload: { email, isSearchMode },
  }),

  setSearchedUserName: (name: string): AppAction => ({
    type: ActionTypes.SET_SEARCHED_USER_NAME,
    payload: name,
  }),

  setShowLogoutPage: (show: boolean): AppAction => ({
    type: ActionTypes.SET_SHOW_LOGOUT_PAGE,
    payload: show,
  }),

  setDateRange: (dateRange: DateRange): AppAction => ({
    type: ActionTypes.SET_DATE_RANGE,
    payload: dateRange,
  }),

  clearUserCache: (email: string): AppAction => ({
    type: ActionTypes.CLEAR_USER_CACHE,
    payload: { email },
  }),

  clearAllCache: (): AppAction => ({
    type: ActionTypes.CLEAR_ALL_CACHE,
  }),

  resetAllServices: (): AppAction => ({
    type: ActionTypes.RESET_ALL_SERVICES,
  }),

  resetAndStartLoading: (services: readonly string[]): AppAction => ({
    type: ActionTypes.RESET_AND_START_LOADING,
    payload: { services },
  }),
};
